import React, { useState, useMemo } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Avatar } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { UserDto, UserRole, UserStatus } from '@wellverse/shared-ts';

const roleColors: Record<UserRole, string> = {
  [UserRole.USER]: '#898989',
  [UserRole.MODERATOR]: '#3ecf8e',
  [UserRole.ADMIN]: '#f5a623',
  [UserRole.TRAINER]: '#1890ff',
};

const statusColors: Record<UserStatus, string> = {
  [UserStatus.ACTIVE]: '#3ecf8e',
  [UserStatus.INACTIVE]: '#898989',
  [UserStatus.PENDING]: '#f5a623',
  [UserStatus.SUSPENDED]: '#ff6b6b',
};

export const UserList: React.FC = () => {
  const { tableProps } = useTable<UserDto>({
    resource: 'users',
    pagination: {
      pageSize: 20,
    },
  });

  const [searchText, setSearchText] = useState('');

  const filteredData = useMemo(() => {
    if (!searchText) return tableProps.dataSource;
    const lower = searchText.toLowerCase();
    return tableProps.dataSource?.filter((item: any) =>
      item.email?.toLowerCase().includes(lower) ||
      item.phone?.toLowerCase().includes(lower) ||
      item.firstName?.toLowerCase().includes(lower) ||
      item.lastName?.toLowerCase().includes(lower) ||
      item.fullName?.toLowerCase().includes(lower)
    );
  }, [tableProps.dataSource, searchText]);

  return (
    <List resource="users">
      <div style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by email, phone, name..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 320 }}
          allowClear
        />
      </div>
      <Table {...tableProps} dataSource={filteredData} rowKey="id">
        <Table.Column
          title="User"
          width={200}
          render={(_: unknown, record: UserDto) => (
            <Space>
              <Avatar
                src={record.avatar}
                icon={!record.avatar && <UserOutlined />}
                size={32}
              />
              <div>
                <div style={{ fontWeight: 500 }}>{record.fullName}</div>
                <div style={{ fontSize: 12, color: '#898989' }}>
                  {record.email || record.phone || '—'}
                </div>
              </div>
            </Space>
          )}
        />
        <Table.Column
          dataIndex="role"
          title="Role"
          width={120}
          filters={Object.values(UserRole).map((r) => ({ text: r, value: r }))}
          onFilter={(value, record: any) => record.role === value}
          render={(role: UserRole) => (
            <Tag
              style={{
                borderColor: roleColors[role],
                color: roleColors[role],
                background: `${roleColors[role]}15`,
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {role}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          width={110}
          filters={Object.values(UserStatus).map((s) => ({ text: s, value: s }))}
          onFilter={(value, record: any) => record.status === value}
          render={(status: UserStatus) => (
            <Tag
              style={{
                borderColor: statusColors[status] || '#898989',
                color: statusColors[status] || '#898989',
                background: `${statusColors[status] || '#898989'}15`,
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {status}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="referralCode"
          title="Referral Code"
          width={150}
          render={(code?: string) => code ?? '—'}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created At"
          width={160}
          render={(date: string) =>
            date ? new Date(date).toLocaleString() : '—'
          }
        />
        <Table.Column
          title="Actions"
          width={120}
          dataIndex="actions"
          render={(_: unknown, record: UserDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="users" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="users" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="users" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
